// common/services/player/playerWaiver.ts
import req from '../../api/request'
import { ENDPOINTS } from '../../api/endpoints'

/*
 * GET /players/:id/waivers/:waiverId — one waiver signature in full, for the
 * admin Player Details screen. Names are the snapshots taken at signing.
 */

export interface PlayerWaiverText {
  id: string
  name: string
  version: number
  /** The waiver as the signer was shown it (HTML). */
  content: string
}

export interface PlayerWaiverDocument {
  filename: string
  mime_type: string
  size_bytes: number
}

export interface PlayerWaiverDetails {
  id: string
  /** `signed` or `revoked`. */
  status: string
  /** `invitation`, `registration`, `admin_attestation`, `external_import` or `legacy_backfill`. */
  source: string | null
  evidence_level: string | null
  signature_method: string | null
  /** Where an imported signature came from, e.g. `docusign:<envelope id>`. */
  external_reference: string | null
  /** ISO instant. */
  signed_at: string | null
  schedule: string | null
  schedule_id: string | null
  organization: string | null
  organization_id: string | null
  team: string | null
  participant: {
    name: string | null
    /** `YYYY-MM-DD`. */
    birth_date: string | null
    email: string | null
    is_minor: boolean | null
  }
  signer: {
    name: string | null
    email: string | null
    relationship: string | null
  }
  attestation_note: string | null
  waivers: PlayerWaiverText[]
  revocation: {
    revoked_at: string | null
    revoked_by: string | null
    reason: string | null
    note: string | null
  } | null
  /** The signed copy on file; null when none is archived. */
  document: PlayerWaiverDocument | null
}

/** GET /players/:playerId/waivers/:waiverId — admin only (403 otherwise). */
export const getPlayerWaiver = async (
  playerId: string,
  waiverId: string
): Promise<PlayerWaiverDetails> => {
  const { data } = await req(
    ENDPOINTS.PLAYERS.GET_PLAYER_WAIVER(playerId, waiverId)
  )
  return data as PlayerWaiverDetails
}

/** GET …/document — the signed copy itself (404 when none is archived). */
export const getPlayerWaiverDocument = async (
  playerId: string,
  waiverId: string
): Promise<Blob> => {
  const response = await req(
    ENDPOINTS.PLAYERS.GET_PLAYER_WAIVER_DOCUMENT(playerId, waiverId),
    { parseJSON: false, headers: { Accept: 'application/pdf' } }
  )
  return (response as Response).blob()
}
